import { useState, useEffect } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Settings, Layers } from "lucide-react";
import CalendarColorsSection from "@/components/admin/CalendarColorsSection";
import OrderTypeTemplatesDialog from "@/components/order_types/OrderTypeTemplatesDialog";

export const OrderTypesPage = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [templatesTarget, setTemplatesTarget] = useState(null);
  const [formData, setFormData] = useState({ name: "", daily_rate: "", default_vat_rate: 20, target_margin_percent: 30, color: "#3b82f6", text_color: "white" });

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await api.getOrderTypes();
      setTypes(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const resetForm = () => {
    setFormData({ name: "", daily_rate: "", default_vat_rate: 20, target_margin_percent: 30, color: "#3b82f6", text_color: "white" });
    setEditingType(null);
  };

  const openDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        daily_rate: type.daily_rate,
        default_vat_rate: type.default_vat_rate ?? 20,
        target_margin_percent: type.target_margin_percent ?? 30,
        color: type.color || "#3b82f6",
        text_color: type.text_color || "white",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        daily_rate: parseFloat(formData.daily_rate),
        default_vat_rate: parseFloat(formData.default_vat_rate) || 20,
        target_margin_percent: parseFloat(formData.target_margin_percent) || 30,
        color: formData.color || null,
        text_color: formData.text_color || "white",
      };

      if (editingType) {
        await api.updateOrderType(editingType.id, data);
        toast.success("Type mis à jour");
      } else {
        await api.createOrderType(data);
        toast.success("Type créé");
      }
      setDialogOpen(false);
      resetForm();
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce type de commande ?")) return;
    try {
      await api.deleteOrderType(id);
      toast.success("Type supprimé");
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="order-types-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Types de commandes</h1>
          <p className="text-muted-foreground mt-1">
            Configurez les types de chantier et leurs taux journaliers
          </p>
        </div>
        <Button onClick={() => openDialog()} data-testid="add-type-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau type
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">Calcul de la durée estimée</h3>
              <p className="text-sm text-muted-foreground mt-1">
                La durée d'un chantier est calculée automatiquement : <strong>Montant HT ÷ Taux journalier</strong>.
                <br />
                Cette durée est ensuite divisée par le nombre d'exécutants assignés à la commande.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Types List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : types.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Aucun type de commande</p>
            <Button className="mt-4" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Créer le premier type
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {types.map((type) => (
            <Card key={type.id} className="card-hover" data-testid={`type-card-${type.id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-4 w-4 rounded-full border"
                      style={{ backgroundColor: type.color || "#cbd5e1" }}
                      data-testid={`type-color-swatch-${type.id}`}
                      title={type.color || "Aucune couleur configurée"}
                    />
                    {type.name}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setTemplatesTarget(type)}
                      data-testid={`templates-type-${type.id}`}
                      title="Produits type"
                    >
                      <Layers className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(type)}
                      data-testid={`edit-type-${type.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(type.id)}
                      data-testid={`delete-type-${type.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-heading font-bold text-primary currency">
                    {formatCurrency(type.daily_rate)}
                  </span>
                  <span className="text-muted-foreground">/ jour</span>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Exemple : Une commande de 15 000€ = {(15000 / type.daily_rate).toFixed(1)} jours
                </p>
                <div className="mt-3 pt-3 border-t text-xs text-muted-foreground flex items-center justify-between">
                  <span>TVA par défaut</span>
                  <span className="font-semibold text-foreground" data-testid={`type-vat-${type.id}`}>
                    {(type.default_vat_rate ?? 20).toString().replace(".", ",")}%
                  </span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground flex items-center justify-between">
                  <span>Marge cible</span>
                  <span className="font-semibold text-emerald-700" data-testid={`type-margin-${type.id}`}>
                    {(type.target_margin_percent ?? 30).toString().replace(".", ",")}%
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CalendarColorsSection />

      <OrderTypeTemplatesDialog
        open={!!templatesTarget}
        orderType={templatesTarget}
        onClose={() => setTemplatesTarget(null)}
      />

      {/* Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? "Modifier le type" : "Nouveau type de commande"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du type</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Installation, Réparation..."
                required
                data-testid="type-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="daily_rate">Taux journalier (€/jour)</Label>
              <Input
                id="daily_rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.daily_rate}
                onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                placeholder="Ex: 1500"
                required
                data-testid="type-rate-input"
              />
              <p className="text-xs text-muted-foreground">
                Ce taux sera utilisé pour calculer la durée estimée des chantiers
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_vat_rate">Taux de TVA par défaut</Label>
              <Select
                value={String(formData.default_vat_rate ?? 20)}
                onValueChange={(v) => setFormData({ ...formData, default_vat_rate: parseFloat(v) })}
              >
                <SelectTrigger data-testid="type-vat-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5.5">5,5%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Pré-rempli automatiquement lors de la création d'une commande (modifiable par commande).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_margin_percent">Marge cible (%)</Label>
              <Input
                id="target_margin_percent"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.target_margin_percent}
                onChange={(e) => setFormData({ ...formData, target_margin_percent: e.target.value })}
                placeholder="Ex : 40 ou 60"
                data-testid="type-margin-input"
              />
              <p className="text-xs text-muted-foreground">
                Utilisée sur la page Commandes pour afficher un voyant "Rentable / Déficitaire"
                (CA HT − coût matière − coût main-d'œuvre comparé à cette cible).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type-color">Couleur dans les calendriers</Label>
              <div className="flex items-center gap-3">
                <input
                  id="type-color"
                  type="color"
                  value={formData.color || "#3b82f6"}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10 w-16 rounded border cursor-pointer"
                  data-testid="type-color-picker"
                />
                <Input
                  type="text"
                  value={formData.color || "#3b82f6"}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 font-mono text-sm"
                  placeholder="#3b82f6"
                />
                <div
                  className="h-10 w-32 rounded border flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: formData.color || "#3b82f6",
                    color: formData.text_color === "black" ? "#0f172a"
                      : formData.text_color === "gray" ? "#475569"
                      : "#ffffff",
                  }}
                >
                  Aperçu
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Couleur de la bulle d'une commande de ce type dans les calendriers (planning + suivi).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type-text-color">Couleur du texte sur la bulle</Label>
              <Select
                value={formData.text_color || "white"}
                onValueChange={(v) => setFormData({ ...formData, text_color: v })}
              >
                <SelectTrigger data-testid="type-text-color-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="white">Blanc</SelectItem>
                  <SelectItem value="gray">Gris</SelectItem>
                  <SelectItem value="black">Noir</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                À ajuster selon la couleur de fond pour une lisibilité optimale.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="type-submit-btn">
                {editingType ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderTypesPage;
